import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Row, Space, Spin } from 'antd';
import { useBreadcrumbRoutes, Wrapper, Button, Select, useUpdate, FormTitle, Modal } from '@maxtropy/components';
import styles from './index.module.scss';
import { useParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import EditPeriodModal from './EditPeriodModal';
import { apiV2PeriodDetailPost, apiV2PeriodDetailRemoveMonthPost } from '@maxtropy/device-customer-apis-v2';
import FastAddPeriodModal from './FastAddPeriodModal';
import ImportPeriodModal from './ImportPeriodModal';
import { DatePeriodItem, DayPeriodItem } from '../utils';
import { PlusOutlined } from '@ant-design/icons';

export const ColorList = ['#ABD335', '#52E7FF', '#FF9247', '#16DD8E', '#CE90D1', '#FF477B', '#2D8DFF'];

const routes = [{ name: '配置时间段' }];

const ConfigQueryPeriod = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const { id } = useParams();
  const [updateState, forceUpdate] = useUpdate();
  const [panelValue, setPanelValue] = useState<Dayjs>(dayjs());
  const [dayPeriodMonth, setDayPeriodMonth] = useState<DayPeriodItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const [showFastAddModal, setShowFastAddModal] = useState<boolean>(false);
  const [openImportModal, setOpenImportModal] = useState<boolean>(false);

  const [currentMonth, setCurrentMonth] = useState<number>();
  const [currentDate, setCurrentDate] = useState<number>();
  const [periodList, setPeriodList] = useState<DatePeriodItem[]>([]);

  const panelChangeRef = React.useRef(false);

  // 生成时间段颜色映射的函数
  const generatePeriodColorMap = (dayPeriodMonth: DayPeriodItem[]) => {
    const colorMap = new Map<string, string>();
    let colorIndex = 0;

    dayPeriodMonth.forEach(day => {
      day.timeList?.forEach(period => {
        const periodKey = `${period.startTime}-${period.endTime}`;
        if (!colorMap.has(periodKey)) {
          colorMap.set(periodKey, ColorList[colorIndex % ColorList.length]);
          colorIndex++;
        }
      });
    });

    return colorMap;
  };

  // 根据 panelValue 更新 currentMonth
  useEffect(() => {
    setCurrentMonth(panelValue.startOf('month').valueOf());
  }, [panelValue]);

  // 获取当前用能单元下的所有班组
  useEffect(() => {
    if (id) {
      apiV2PeriodDetailPost({
        dataQueryPeriodId: id,
        startTime: dayjs(currentMonth).startOf('month').valueOf(),
        endTime: dayjs(currentMonth).add(1, 'month').startOf('month').valueOf(),
      }).then(res => {
        setDayPeriodMonth(res?.list ?? []);
      });
    }
  }, [id, currentMonth, updateState]);

  const periodColorMap = useMemo(() => generatePeriodColorMap(dayPeriodMonth), [dayPeriodMonth]);

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), ...routes]} className={styles.wrapper}>
      <FormTitle title="配置时间段" />

      <Space size={8}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setShowFastAddModal(true);
          }}
        >
          快捷新增时段
        </Button>
        <Button
          type="primary"
          onClick={() => {
            setOpenImportModal(true);
          }}
        >
          导入
        </Button>
        <Button
          type="primary"
          onClick={() => {
            Modal.confirm({
              title: '当前操作会清空已配置的时段数据，确认是否清空？',
              okText: '确定',
              onOk() {
                apiV2PeriodDetailRemoveMonthPost({
                  dataQueryPeriodId: id,
                  currentMonth: currentMonth,
                }).then(res => {
                  forceUpdate();
                });
              },
            });
          }}
        >
          清空当月
        </Button>
      </Space>

      <div className={styles.calendar}>
        <Spin spinning={loading}>
          <Calendar
            value={panelValue}
            onPanelChange={v => {
              panelChangeRef.current = true;
              setPanelValue(v);
            }}
            fullCellRender={current => {
              const date = dayjs(current).format('DD');
              const cellDate = dayjs(current).format('YYYY-MM-DD');

              if (panelValue.month() !== current.month()) {
                return null;
              }

              const periodList =
                dayPeriodMonth.find(k => dayjs(k.currentDay).format('YYYY-MM-DD') === cellDate)?.timeList ?? [];

              return (
                <div className={styles.dateCard}>
                  <div className={styles.innerBox}>
                    <div className={styles.header}>
                      <Button
                        type="link"
                        className={styles.editButton}
                        style={{ fontSize: 12, padding: 0 }}
                        onClick={() => {
                          setPeriodList(periodList);
                          setCurrentDate(dayjs(current).valueOf());
                          setShowEditModal(true);
                        }}
                      >
                        编辑
                      </Button>
                      <div className={styles.date}>{date}</div>
                    </div>
                    <div className={styles.list}>
                      {periodList.length ? (
                        <>
                          {periodList?.map((item, index) => {
                            const periodKey = `${item.startTime}-${item.endTime}`;
                            const textColor = periodColorMap.get(periodKey);
                            const backColor = textColor + '1A';
                            const borderColor = textColor + '80';
                            return (
                              <div
                                className={styles.period}
                                key={index}
                                style={{ color: textColor, backgroundColor: backColor, borderColor: borderColor }}
                              >
                                {item.startTime}-{item.endTime}
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <div style={{ color: 'var(--mx-text-disabled-color)' }}>暂无配置</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            }}
            headerRender={({ value }) => {
              const start = 0;
              const end = 12;
              const monthOptions = [];
              let current = value.clone();
              const localeData = value.localeData();
              const months = [];
              for (let i = 0; i < 12; i++) {
                current = current.month(i);
                months.push(localeData.monthsShort(current));
              }
              for (let i = start; i < end; i++) {
                monthOptions.push(
                  <Select.Option key={i} value={i} className="month-item">
                    {months[i]}
                  </Select.Option>
                );
              }
              const year = value.year();
              const month = value.month();
              const options = [];
              for (let i = year - 10; i < year + 10; i += 1) {
                options.push(
                  <Select.Option key={i} value={i} className="year-item">
                    {i}
                  </Select.Option>
                );
              }

              return (
                <Row gutter={8} justify="end" align="middle" style={{ paddingBottom: 8 }}>
                  <Space size={8}>
                    <div>时间选择</div>
                    <Select
                      size="middle"
                      style={{ width: 120 }}
                      value={year}
                      onChange={newYear => {
                        const now = value.clone().year(newYear);
                        setPanelValue(now);
                      }}
                    >
                      {options}
                    </Select>
                    <Select
                      size="middle"
                      style={{ width: 120 }}
                      value={month}
                      onChange={newMonth => {
                        const now = value.clone().month(newMonth);
                        setPanelValue(now);
                      }}
                    >
                      {monthOptions}
                    </Select>
                  </Space>
                </Row>
              );
            }}
          />
        </Spin>
      </div>

      {showEditModal && (
        <EditPeriodModal
          currentDate={currentDate!}
          periodList={periodList}
          modalVisible={showEditModal}
          setModalVisible={() => {
            setShowEditModal(false);
          }}
          forceUpdate={forceUpdate}
        />
      )}

      {showFastAddModal && (
        <FastAddPeriodModal
          monthPeriodList={dayPeriodMonth}
          currentMonth={currentMonth!}
          modalVisible={showFastAddModal}
          setModalVisible={() => {
            setShowFastAddModal(false);
          }}
          forceUpdate={forceUpdate}
        />
      )}

      {openImportModal && (
        <ImportPeriodModal
          open={openImportModal}
          closeModal={() => {
            setOpenImportModal(false);
          }}
          forceUpdate={forceUpdate}
        />
      )}
    </Wrapper>
  );
};

export default ConfigQueryPeriod;
